import {
  Box,
  Button,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { updatePricing, createPricing } from "../../../api/services/api";

interface ServicePricingEditFormProps {
  clinicianId: number;
  serviceId: number;
  consultationMode: string;
  initialData?: {
    id?: number;
    price: number;
    isActive: boolean;
  };
  onClose: () => void;
}

export const ServicePricingEditForm = ({
  clinicianId,
  serviceId,
  consultationMode,
  initialData,
  onClose,
}: ServicePricingEditFormProps) => {
  const queryClient = useQueryClient();
  const [price, setPrice] = useState(initialData?.price || 0);
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);

  const { mutate: updatePricingMutate, isLoading: isUpdating } = useMutation(
    initialData?.id ? updatePricing : createPricing,
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("pricing");
        queryClient.invalidateQueries("pricing-stats");
        queryClient.invalidateQueries("clinician-pricing");
        toast.success(
          res?.data?.message ||
            `Pricing ${initialData?.id ? "updated" : "created"} successfully`
        );
        onClose();
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message ||
            `Failed to ${initialData?.id ? "update" : "create"} pricing`
        );
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (price <= 0) {
      toast.error("Price must be greater than 0");
      return;
    }

    if (initialData?.id) {
      updatePricingMutate({
        id: initialData.id,
        body: {
          price,
          isActive,
        },
      });
    } else {
      updatePricingMutate({
        clinicianId,
        serviceId,
        consultationMode,
        price,
        isActive,
      });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" mb={2}>
        {initialData?.id ? "Edit" : "Create"} Pricing
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Consultation Mode: <strong>{consultationMode}</strong>
      </Typography>

      <TextField
        fullWidth
        label="Price (SAR)"
        type="number"
        value={price}
        onChange={(e) => setPrice(parseFloat(e.target.value))}
        inputProps={{ min: 0, step: 0.01 }}
        required
        sx={{ mb: 2 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        }
        label="Active"
        sx={{ mb: 3 }}
      />

      <Box display="flex" gap={2}>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isUpdating}
        >
          {isUpdating ? (
            <CircularProgress size={24} />
          ) : initialData?.id ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
        <Button variant="outlined" fullWidth onClick={onClose}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
